/**
 * httpError — the ONE place the frontend reads the backend's canonical error
 * envelope and threads the consent-gate `?student_id=` query param.
 *
 * WHY THIS MODULE EXISTS (H4 error-contract unify #68): the admissions and
 * career API modules each carried a byte-for-byte copy of `withStudent` (the
 * `?student_id=` threader) and a near-copy of `friendlyError` (the human-message
 * reader). Two copies of a contract reader is a drift hazard: a fix to one
 * (e.g. admissions' `friendlyError` only read the LEGACY top-level `detail`,
 * missing the envelope's `error.message` that errors.py actually emits in prod)
 * silently left the other stale. Hoisting both here gives every product surface
 * ONE reader of the wire contract, so they branch identically on the same
 * backend shape.
 *
 * THE ERROR ENVELOPE (the contract): the backend wraps EVERY error via
 * errors.py `setup_exception_handlers` as
 *   { "error": { "code": <str>, "message": <str>, "details"?: <obj|list> } }
 * There is NO top-level `detail` under this envelope. A handful of legacy
 * surfaces / tests still mock the FastAPI default (`{ detail: <str|list> }`),
 * so the readers DUAL-READ: envelope first, legacy `detail` as a fallback. This
 * keeps the migration safe — nothing regresses while every surface moves onto
 * the envelope.
 *
 * The envelope carries STRUCTURED bodies too: a route that raises
 * `HTTPException(status, detail={...})` has its dict copied verbatim into
 * `error.message`. `readStructuredDetail` is the reader for those machine
 * contracts (see its docstring); `readMessage`/`friendlyError` stay the readers
 * for human prose.
 *
 * coppa/coppaApi.js keeps its RICHER variants (errorCode / errorMessage /
 * errorScope and the gate classifiers) — those read additional envelope fields
 * the COPPA interceptor needs; its `friendlyError` is the same canonical
 * behavior and may delegate here.
 */

/**
 * The human `message` from the structured error envelope, dual-reading the
 * legacy top-level `detail` string. Returns '' when neither is a usable string
 * (callers treat '' as "no server-supplied message"). The Career composite-gate
 * denial's envelope code is the generic 'FORBIDDEN', so this message is one of
 * the signals that distinguishes it — read it from wherever the backend put it.
 */
export function readMessage(err) {
  const structured = err?.response?.data?.error?.message;
  if (typeof structured === 'string' && structured.trim()) return structured;
  const legacy = err?.response?.data?.detail;
  return typeof legacy === 'string' ? legacy : '';
}

/**
 * The STRUCTURED (object) error body, dual-read the same way `readMessage`
 * dual-reads strings. (2026-08-05, cross-vendor audit finding.)
 *
 * Some routes raise `HTTPException(status, detail={...})` — a MACHINE contract,
 * not prose: e.g. the prep walkthrough's 409s
 *   `{code: "step_order_enforced", resume_step: N, message: "..."}` and
 *   `{action: "start_over", ...}`,
 * which the client branches on. errors.py `http_exception_handler` copies that
 * dict verbatim into `error.message`, so in production the object lives at
 * `data.error.message` and there is NO top-level `detail` — a reader that only
 * knew `data.detail` passes every test (bare TestClient/dev shape) and NEVER
 * fires against the running app, silently degrading a designed branch into the
 * generic error path.
 *
 * Object bodies ONLY, deliberately: unwrapping `error.message` for arbitrary
 * statuses is `readMessage`/`friendlyError`'s job, and those apply the
 * string-safety rules. Returns null when the body isn't a structured contract
 * (missing, a string, or an array — the 422 list shape), so callers can safely
 * `if (body && body.code === …)` and fall through to their generic handling.
 */
export function readStructuredDetail(err) {
  const data = err?.response?.data;
  if (!data) return null;
  // Legacy/bare shape first when present (it is the raw detail verbatim),
  // then the canonical envelope — either way we get the dict the route raised.
  const body = data.detail != null ? data.detail : data.error?.message;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  return body;
}

/**
 * The MACHINE error code from the canonical envelope (`error.code`), or ''.
 *
 * The envelope errors.py emits for an `AppException` is
 *   { "error": { "code": "<machine code>", "message": "<human prose>",
 *                "details": { ... } } }
 * — i.e. the machine contract lives in `code`/`details` while `message` is
 * prose, so `readStructuredDetail` (which unwraps `error.message` for the
 * routes that raise `HTTPException(status, detail={...})`) CANNOT see it. A
 * client branching on an AppException code (e.g. the shortlist's 409
 * `has_milestones` confirm gate) must read it here.
 *
 * DUAL-READ, same discipline as `readMessage`: the canonical envelope first,
 * then a legacy/bare `detail` OBJECT carrying its own `code`. Returns '' when
 * there is no machine code, so callers can safely compare with `===`.
 */
export function readErrorCode(err) {
  const enveloped = err?.response?.data?.error?.code;
  if (typeof enveloped === 'string' && enveloped.trim()) return enveloped;
  const legacy = readStructuredDetail(err)?.code;
  return typeof legacy === 'string' ? legacy : '';
}

/**
 * The structured `details` object that travels WITH an envelope `error.code`
 * (e.g. `{ milestone_count: 3 }` on the shortlist's 409), or null.
 *
 * Falls back to the legacy/bare structured `detail` body so a route that
 * raised `HTTPException(status, detail={...})` — and any test mocking that
 * shape — reads identically. Never returns a string or an array (those are
 * prose / the Pydantic-422 list, handled by `friendlyError`).
 */
export function readErrorDetails(err) {
  const enveloped = err?.response?.data?.error?.details;
  if (enveloped && typeof enveloped === 'object' && !Array.isArray(enveloped)) {
    return enveloped;
  }
  return readStructuredDetail(err);
}

/**
 * A user-friendly message for a write/validation failure, NEVER a raw error
 * object or stack. Reads the canonical envelope FIRST (`error.message` — what
 * errors.py emits in prod), then falls back to the legacy top-level `detail`
 * string, then a Pydantic-422 details list (`[{loc, msg, type}]` → first `msg`),
 * then the supplied default. This is the canonical implementation the per-
 * product `friendlyError` re-exports, so every product's write errors surface
 * the same way regardless of which error shape the backend used.
 */
export function friendlyError(err, fallback) {
  const msg = readMessage(err);
  if (msg && msg.trim()) return msg;
  // Legacy / 422 fallbacks read the top-level `detail` (the FastAPI default
  // shape) for the surfaces or tests still on it.
  const legacy = err?.response?.data?.detail;
  if (typeof legacy === 'string' && legacy.trim()) return legacy;
  if (Array.isArray(legacy) && legacy.length > 0 && legacy[0]?.msg) {
    return legacy[0].msg;
  }
  return fallback || 'Something went wrong. Please try again.';
}

/**
 * Build a `withStudent(path, studentId)` threader bound to a `base` URL. A
 * guardian acting on a verified-linked minor passes the minor's UUID, which the
 * gate dependency reads as `?student_id=`; omitted → the caller acts as
 * themselves. Built with URLSearchParams so a stray value can't break the URL.
 *
 * A FACTORY (not a bare function) because the only difference between the
 * admissions and career copies was the `base` constant — closing over it here
 * removes the duplication while keeping each module's `withStudent(path, id)`
 * call sites unchanged.
 *
 * SEPARATOR: `path` may ALREADY carry a query string (e.g. interviewApi's
 * `/questions?track=…` and `/projects?track=…`), so we join `student_id` with
 * `&` when a `?` is already present and `?` otherwise. Hardcoding `?` produced a
 * malformed double-`?` URL (`/questions?track=x?student_id=y`) on those paths,
 * which silently DROPPED `student_id` — so the gate read the GUARDIAN's identity
 * instead of the linked minor's. Robustly picking the separator is the fix.
 */
export function makeWithStudent(base) {
  return function withStudent(path, studentId) {
    if (!studentId) return `${base}${path}`;
    const qs = new URLSearchParams({ student_id: studentId }).toString();
    const sep = path.includes('?') ? '&' : '?';
    return `${base}${path}${sep}${qs}`;
  };
}
